import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Box,
  Typography,
  alpha,
} from "@mui/material";
import { Delete as DeleteIcon } from "@mui/icons-material";
import DialogLoading from "../../App/DialogLoading";

export const DeleteMessage = ({ body1, body2 }) => {
  return (
    <>
      <Typography
        variant='body1'
        color='text.primary'
        align='justify'
        gutterBottom
      >
        {body1}
      </Typography>
      {body2 && body2 !== "" && (
        <Typography variant='body2' color='text.secondary' sx={{ mt: 2 }}>
          {body2}
        </Typography>
      )}
    </>
  );
};

const RemoveDialog = ({
  icon: Icon,
  open,
  onClose,
  title,
  onConfirm,
  loading,
  children,
  confirmText,
}) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby='remove-dialog-title'
      aria-describedby='remove-dialog-description'
      maxWidth='sm'
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: "0 12px 40px rgba(0, 0, 0, 0.18)",
          border: (theme) =>
            `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
          maxWidth: { xs: "95%", sm: 520 },
          minWidth: { xs: "90%", sm: 420 },
          position: "relative",
          overflow: "hidden",
        },
      }}
    >
      <Box
        sx={{
          height: 6,
          borderTopLeftRadius: 2,
          borderTopRightRadius: 2,
        }}
      />
      <DialogTitle
        id='remove-dialog-title'
        sx={{
          fontWeight: 700,
          fontSize: { xs: "1.25rem", sm: "1.5rem" },
          py: 2.5,
          px: 3,
          display: "flex",
          alignItems: "flex-start",
          gap: 2,
          color: "text.primary",
        }}
      >
        <Box
          sx={{
            fontSize: { xs: 32, sm: 36 },
            color: "warning.main",
            mt: 0.5,
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
          }}
        >
          {Icon ? (
            <Icon color='warning' fontSize='inherit' />
          ) : (
            <DeleteIcon color='warning' fontSize='inherit' />
          )}
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant='h5' fontWeight={700} color='error.main'>
            {title}
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            This action cannot be undone.
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent id='remove-dialog-description' sx={{ px: 3, py: 2 }}>
        {loading ? <DialogLoading /> : children}
      </DialogContent>

      <DialogActions
        sx={{
          p: 2.5,
          pt: 1.5,
          borderTop: "1px solid",
          borderColor: "divider",
          gap: 1.5,
          flexDirection: { xs: "column-reverse", sm: "row" },
          "& > *": { minWidth: { xs: "100%", sm: "auto" } },
        }}
      >
        <Button
          onClick={onClose}
          variant='outlined'
          size='large'
          sx={{
            borderRadius: 2,
            textTransform: "none",
            fontWeight: 500,
            fontSize: "1rem",
            py: 1.1,
            borderColor: "grey.300",
            "&:hover": {
              borderColor: "primary.main",
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.06),
              boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
            },
            transition: "all 0.2s ease",
          }}
          disabled={loading}
        >
          Cancel
        </Button>
        <Button
          onClick={onConfirm}
          variant='contained'
          size='large'
          autoFocus
          sx={{
            borderRadius: 2,
            textTransform: "none",
            fontWeight: 600,
            fontSize: "1.0625rem",
            py: 1.2,
            px: 4,
            position: "relative",
            bgcolor: "error.main",
            "&:hover": {
              bgcolor: "error.dark",
              boxShadow: "0 4px 16px rgba(244, 67, 54, 0.4)",
            },
            "&.Mui-disabled": {
              bgcolor: (theme) => alpha(theme.palette.error.main, 0.5),
              color: "common.white",
            },
            transition: "all 0.25s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
            ...(loading && {
              minWidth: 120,
              "& .MuiCircularProgress-root": {
                color: "common.white",
                position: "absolute",
                left: "50%",
                transform: "translateX(-50%)",
              },
            }),
          }}
          disabled={loading}
        >
          {loading ? (
            <Box sx={{ width: 24, height: 24 }}>
              <CircularProgress size={24} thickness={4} />
            </Box>
          ) : (
            confirmText || title
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RemoveDialog;
